import type { Amount, SignInfo } from '@shared/core-types';
import type { PayeeVerificationResult, RecipientAddress } from '@api-client/payments-webapi';

import type {
    Authorization,
    HrPaymentSpecifics,
    LegalEntityIdentifiers,
    PaymentAddress,
    RsPaymentSpecifics,
} from './PaymentOrderTypes';
import type { PurposeCode, Receiver, STANDING_ORDER_TYPE, Sender } from '../Payments';
import type { Symbols } from '../TransferTemplate';

export enum STANDING_ORDER_FLAG {
    EDITABLE = 'editable',
    DELETABLE = 'deletable',
    CANCELABLE = 'cancelable',
    STOP_REGULAR_EXECUTIONS_AFTER_DEATH = 'stopRegularExecutionsAfterDeath',
}

export enum EXECUTION_MODE {
    UNTIL_CANCELLATION = 'UNTIL_CANCELLATION',
    UNTIL_DATE = 'UNTIL_DATE',
    AFTER_MAX_ITERATION_EXCEEDED = 'AFTER_MAX_ITERATION_EXCEEDED',
    AFTER_MAX_AMOUNT_EXCEEDED = 'AFTER_MAX_AMOUNT_EXCEEDED',
}

export enum EXECUTION_DUE_MODE {
    DUE_DAY_OF_MONTH = 'DUE_DAY_OF_MONTH',
    DUE_LAST_DAY_OF_MONTH = 'DUE_LAST_DAY_OF_MONTH',
}

export enum EXECUTION_DUE_TYPE {
    BEFORE_DUE_DATE = 'BEFORE_DUE_DATE',
    AFTER_DUE_DATE = 'AFTER_DUE_DATE',
}

export enum STOPPAGE {
    JANUARY = 'JANUARY',
    FEBRUARY = 'FEBRUARY',
    MARCH = 'MARCH',
    APRIL = 'APRIL',
    MAY = 'MAY',
    JUNE = 'JUNE',
    JULY = 'JULY',
    AUGUST = 'AUGUST',
    SEPTEMBER = 'SEPTEMBER',
    OCTOBER = 'OCTOBER',
    NOVEMBER = 'NOVEMBER',
    DECEMBER = 'DECEMBER',
}

export enum SUB_TYPE {
    SWEEP_OVER_LIMIT = 'SWEEP_OVER_LIMIT',
    SWEEP_UNDER_LIMIT = 'SWEEP_UNDER_LIMIT',
    SWEEP_FIX_LIMIT = 'SWEEP_FIX_LIMIT',
}

export enum EXECUTION_INTERVAL {
    DAILY = 'DAILY',
    WEEKLY = 'WEEKLY',
    MONTHLY = 'MONTHLY',
    QUARTERLY = 'QUARTERLY',
    BI_MONTHLY = 'BI_MONTHLY',
    SEMI_MONTHLY = 'SEMI_MONTHLY',
    HALFYEARLY = 'HALFYEARLY',
    SEMI_ANNUAL = 'SEMI_ANNUAL',
    YEARLY = 'YEARLY',
    ANNUALLY = 'ANNUALLY',
    IRREGULAR = 'IRREGULAR',
    RECURRENT = 'RECURRENT',
}

export enum STANDING_ORDER_STATUS {
    OK = 'OK',
    OPEN = 'OPEN',
    ADVISOR_CHANGEABLE = 'ADVISOR_CHANGEABLE',
    CREATE_COLLECTIVE_SIGNING = 'CREATE_COLLECTIVE_SIGNING',
    UPDATE_COLLECTIVE_SIGNING = 'UPDATE_COLLECTIVE_SIGNING',
    DELETE_COLLECTIVE_SIGNING = 'DELETE_COLLECTIVE_SIGNING',
    NEXT_EXECUTION_DATE_AUTOMATICALLY_SET = 'NEXT_EXECUTION_DATE_AUTOMATICALLY_SET',
    NOT_CHANGEABLE = 'NOT_CHANGEABLE',
    IN_EXECUTION = 'IN_EXECUTION',
    DELETED = 'DELETED',
    CREATE_STANDING_ORDER_PENDING = 'CREATE_STANDING_ORDER_PENDING',
    EDIT_STANDING_ORDER_PENDING = 'EDIT_STANDING_ORDER_PENDING',
    DELETE_STANDING_ORDER_PENDING = 'DELETE_STANDING_ORDER_PENDING',
    FAILED = 'FAILED',
}

export enum STANDING_ORDER_STATE {
    OPEN = 'OPEN',
    PENDING = 'PENDING',
    CANCELLED = 'CANCELLED',
    EXPIRED = 'EXPIRED',
}

export enum STANDING_ORDER_STATE_DETAIL {
    PSI = 'PSI',
    STO = 'STO',
    KAG = 'KAG',
    NBA = 'NBA',
    BAN = 'BAN',
    LGR = 'LGR',
    ABG = 'ABG',
}

export interface StandingOrderAdditionalInfo {
    text4x35?: string[];
    otherText?: string;
}

interface StandingOrderBreakField {
    validFromDate?: string;
    validToDate?: string;
}

export interface StandingOrder {
    additionalInfo?: StandingOrderAdditionalInfo;
    alias?: string;
    number?: string;
    id?: string;
    type: STANDING_ORDER_TYPE;
    status?: STANDING_ORDER_STATUS;
    state?: STANDING_ORDER_STATE;
    stateDetail?: STANDING_ORDER_STATE_DETAIL;
    subtype?: SUB_TYPE;
    intendedPurpose: string;
    sender: Sender;

    /* extended account fields */
    senderAccountId?: string;
    senderName?: string;

    startDate?: string;
    executionDueMode: EXECUTION_DUE_MODE;
    flags?: STANDING_ORDER_FLAG[];
    amount: Amount;
    minAmount?: Amount;
    executionInterval: EXECUTION_INTERVAL;
    nextExecutionDate?: string;
    nextExecutionDateTime?: string;
    lastExecutionDate?: string;
    stoppages: STOPPAGE[];
    intervalDueDay?: number;
    intervalDueMonth?: number;
    executionMode: EXECUTION_MODE;
    executionDueType?: EXECUTION_DUE_TYPE;
    paymentReference: string;
    senderReference: string;
    receiver: Receiver;
    receiverAddress?: PaymentAddress;
    recipientAddress?: RecipientAddress & { id?: string };
    receiverName: string;
    officialReceiverName?: string;
    receiverBankName?: string;
    receiverBankAddress?: PaymentAddress;
    symbols?: Symbols;
    break?: StandingOrderBreakField;
    maxIterations?: string;
    maxAmount?: Amount;
    daysOfRepetition?: number;
    realExecutionDate?: string;
    executedAmount?: Amount;
    executedIterations?: string;
    channelId?: string;
    authorizations?: Authorization[];
    signInfo?: SignInfo;
    'hr-paymentSpecifics'?: HrPaymentSpecifics;
    scheduledExecutionDates?: string[];
    applicationId?: string;
    legalEntityIdentifiers?: LegalEntityIdentifiers;
    payeeVerificationResult?: PayeeVerificationResult;
    purposeCode?: PurposeCode;
    'rs-paymentSpecifics'?: RsPaymentSpecifics;
    paymentPurpose?: string;
}
